import React from "react";
import PropTypes from "prop-types";
import { Grid, Box, Typography, Card, Stack } from "@mui/material";
import {
  CasinoOutlined as TotalLotteryIcon,
  CheckCircleOutline as ActiveIcon,
  ArchiveOutlined as ClosedIcon,
  AutorenewOutlined as RolloverIcon,
  AccountBalanceWalletOutlined as PrizeVolumeIcon,
  TrendingUp as TrendingIcon,
} from "@mui/icons-material";
import { colors, borderRadiusPx, customShadows } from "../../../../theme";
import { safeFromWei } from "./AdminLotteryCard";
import { getNetworkCurrency } from "../../../../static/Constants";

/**
 * Single KPI Metric Item
 */
const MetricItem = ({ title, value, subtitle, icon: Icon, color, bgColor, gradient }) => (
  <Card
    sx={{
      p: 2.25,
      borderRadius: borderRadiusPx.xl,
      bgcolor: colors.background.paper,
      border: `1px solid ${colors.dashboard.cardBorder}`,
      boxShadow: customShadows.cardSubtle,
      position: "relative",
      overflow: "hidden",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      transition: "all 0.2s ease",
      "&:hover": {
        boxShadow: customShadows.cardHover,
        borderColor: colors.neutral.borderHover,
        transform: "translateY(-2px)",
      },
    }}
  >
    {/* Subtle Top Indicator Accent */}
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "3px",
        background: gradient || color,
      }}
    />

    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5 }}>
      <Box>
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: 700,
            color: colors.slate[500],
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            mb: 0.5,
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            color: colors.slate[900],
            fontSize: "24px",
            lineHeight: 1.1,
          }}
        >
          {value}
        </Typography>
      </Box>

      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: borderRadiusPx.lg,
          bgcolor: bgColor,
          color: color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 22 }} />
      </Box>
    </Box>

    {subtitle && (
      <Typography sx={{ fontSize: "12px", color: colors.slate[500], fontWeight: 500 }}>
        {subtitle}
      </Typography>
    )}
  </Card>
);

MetricItem.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  subtitle: PropTypes.string,
  icon: PropTypes.elementType.isRequired,
  color: PropTypes.string.isRequired,
  bgColor: PropTypes.string.isRequired,
  gradient: PropTypes.string,
};

/**
 * LotteryStatsCards - Overview metric ribbon displayed above lottery list
 */
const LotteryStatsCards = ({ lotteries = [] }) => {
  // Compute analytics from lotteries array
  const totalCount = lotteries.length;
  
  const openCount = lotteries.filter(
    (l) => l.status === "0" || l.status === 0 || l.status === "5" || l.status === 5
  ).length;

  const closedCount = lotteries.filter(
    (l) => l.status === "1" || l.status === 1 || l.status === "2" || l.status === 2
  ).length;

  const rolloverCount = lotteries.filter(
    (l) =>
      l.status === "3" ||
      l.status === 3 ||
      l.status === "4" ||
      l.status === 4 ||
      l.status === "6" ||
      l.status === 6
  ).length;

  // Calculate total prize pool volume across all lotteries
  const currency = getNetworkCurrency();
  const totalPrizeSum = lotteries.reduce((acc, l) => {
    try {
      const pol = parseFloat(safeFromWei(l.totalPrize));
      return acc + (isNaN(pol) ? 0 : pol);
    } catch {
      return acc;
    }
  }, 0);

  const formattedPrizeSum =
    totalPrizeSum >= 1000
      ? `${(totalPrizeSum / 1000).toFixed(1)}k ${currency}`
      : `${totalPrizeSum.toFixed(2)} ${currency}`;

  return (
    <Grid container spacing={2}>
      {/* Total Lotteries */}
      <Grid item xs={12} sm={6} md={3} lg={2.4}>
        <MetricItem
          title="Total Lotteries"
          value={totalCount}
          subtitle="All created on-chain"
          icon={TotalLotteryIcon}
          color={colors.primary.main}
          bgColor={colors.blue[50]}
          gradient="linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)"
        />
      </Grid>

      {/* Open Lotteries */}
      <Grid item xs={12} sm={6} md={3} lg={2.4}>
        <MetricItem
          title="Active / Open"
          value={openCount}
          subtitle="Ready for participation"
          icon={ActiveIcon}
          color={colors.emerald[600]}
          bgColor={colors.emerald[50]}
          gradient="linear-gradient(135deg, #10b981 0%, #047857 100%)"
        />
      </Grid>

      {/* Closed & Result Done */}
      <Grid item xs={12} sm={6} md={3} lg={2.4}>
        <MetricItem
          title="Closed Draws"
          value={closedCount}
          subtitle="Finished and verified"
          icon={ClosedIcon}
          color={colors.slate[600]}
          bgColor={colors.slate[100]}
          gradient="linear-gradient(135deg, #64748b 0%, #334155 100%)"
        />
      </Grid>

      {/* Rollovers & In Progress */}
      <Grid item xs={12} sm={6} md={3} lg={2.4}>
        <MetricItem
          title="Rollovers / Pending"
          value={rolloverCount}
          subtitle="Rollover & initializing"
          icon={RolloverIcon}
          color={colors.amber[600]}
          bgColor={colors.amber[50]}
          gradient="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
        />
      </Grid>

      {/* Total Prize Pool Volume */}
      <Grid item xs={12} sm={12} md={12} lg={2.4}>
        <MetricItem
          title="Total Prize Pool"
          value={formattedPrizeSum}
          subtitle="Cumulative prize volume"
          icon={PrizeVolumeIcon}
          color={colors.purple[600]}
          bgColor={colors.purple[50]}
          gradient="linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)"
        />
      </Grid>
    </Grid>
  );
};

LotteryStatsCards.propTypes = {
  lotteries: PropTypes.array,
};

export default LotteryStatsCards;
